import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Lock, 
  Save, 
  Shield, 
  Calendar,
  Edit3,
  Key,
  CheckCircle
} from "lucide-react";
import { format } from "date-fns";

const ProfilePage = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    name: "",
    email: ""
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileEditing, setProfileEditing] = useState(false);
  
  // Password form state
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    setProfileData({
      name: user.name || "",
      email: user.email || ""
    });
  }, [user, navigate]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!profileData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    
    if (!profileData.email.trim()) {
      toast.error("Email is required");
      return;
    }

    setProfileLoading(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, {
        name: profileData.name.trim(),
        email: profileData.email.trim()
      }, { withCredentials: true });
      
      // Update local user state
      login({ ...user, ...response.data });
      toast.success("Profile updated successfully!");
      setProfileEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!passwordData.current_password) {
      toast.error("Current password is required");
      return;
    }
    
    if (!passwordData.new_password) {
      toast.error("New password is required");
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.put(`${API}/auth/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { withCredentials: true });
      
      toast.success("Password changed successfully!");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
      setShowPasswordForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const cancelProfileEdit = () => {
    setProfileData({
      name: user?.name || "",
      email: user?.email || ""
    });
    setProfileEditing(false);
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-primary-900 via-primary-800 to-emerald-900 text-white">
        <div className="container-custom py-12">
          <Link 
            to="/" 
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors group"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
          
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-xl ring-4 ring-white/30">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                <User className="w-12 h-12 text-white" />
              )}
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight" data-testid="profile-name">
                {user.name}
              </h1>
              <p className="text-primary-200 mt-1">{user.email}</p>
              <div className="flex items-center gap-3 mt-3">
                {user.is_admin && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-200 text-xs font-medium">
                    <Shield className="w-3 h-3" />
                    Administrator
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs font-medium">
                  <Calendar className="w-3 h-3" />
                  Joined {user.created_at ? format(new Date(user.created_at), "MMMM yyyy") : "Recently"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-10">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Profile Information Card */}
          <Card className="rounded-3xl shadow-lg border-0 overflow-hidden" data-testid="profile-card">
            <div className="bg-gradient-to-r from-primary-50 to-emerald-50 px-6 py-4 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary-700" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-gray-900">Profile Information</h2>
                    <p className="text-sm text-gray-500">Manage your personal details</p>
                  </div>
                </div>
                {!profileEditing && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setProfileEditing(true)}
                    className="rounded-xl"
                    data-testid="edit-profile-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
            <CardContent className="p-6">
              {profileEditing ? (
                <form onSubmit={handleProfileSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-700 font-medium">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="name"
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        placeholder="Your full name"
                        className="pl-12 h-12 rounded-xl"
                        data-testid="profile-name-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700 font-medium">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                        placeholder="your@email.com"
                        className="pl-12 h-12 rounded-xl"
                        data-testid="profile-email-input"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button 
                      type="submit" 
                      className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl px-6"
                      disabled={profileLoading}
                      data-testid="save-profile-btn"
                    >
                      {profileLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={cancelProfileEdit}
                      className="rounded-xl px-6"
                      data-testid="cancel-profile-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-500">Full Name</span>
                    </div>
                    <span className="font-medium text-gray-900" data-testid="profile-display-name">{user.name}</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-500">Email Address</span>
                    </div>
                    <span className="font-medium text-gray-900" data-testid="profile-display-email">{user.email}</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-500">Account Type</span>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      user.is_admin 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {user.is_admin ? 'Administrator' : 'Member'}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Password Card */}
          <Card className="rounded-3xl shadow-lg border-0 overflow-hidden" data-testid="password-card">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Key className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-gray-900">Password & Security</h2>
                    <p className="text-sm text-gray-500">Keep your account secure</p>
                  </div>
                </div>
                {!showPasswordForm && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowPasswordForm(true)}
                    className="rounded-xl"
                    data-testid="change-password-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                )}
              </div>
            </div>
            <CardContent className="p-6">
              {showPasswordForm ? (
                <form onSubmit={handlePasswordSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="current_password" className="text-gray-700 font-medium">Current Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="current_password"
                        type="password"
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                        placeholder="Enter current password"
                        className="pl-12 h-12 rounded-xl"
                        data-testid="current-password-input"
                      />
                    </div>
                  </div>

                  <Separator />
                  
                  <div className="space-y-2">
                    <Label htmlFor="new_password" className="text-gray-700 font-medium">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="new_password"
                        type="password"
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                        placeholder="Enter new password (min 6 characters)"
                        className="pl-12 h-12 rounded-xl"
                        data-testid="new-password-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password" className="text-gray-700 font-medium">Confirm New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="confirm_password"
                        type="password"
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                        placeholder="Confirm new password"
                        className="pl-12 h-12 rounded-xl"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    {passwordData.new_password && passwordData.confirm_password && (
                      <p className={`text-sm flex items-center gap-1 ${
                        passwordData.new_password === passwordData.confirm_password 
                          ? 'text-emerald-600' 
                          : 'text-red-500'
                      }`}>
                        {passwordData.new_password === passwordData.confirm_password ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Passwords match
                          </>
                        ) : (
                          "Passwords don't match"
                        )}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button 
                      type="submit" 
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl px-6"
                      disabled={passwordLoading}
                      data-testid="save-password-btn"
                    >
                      {passwordLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Updating...
                        </>
                      ) : (
                        <>
                          <Key className="w-4 h-4 mr-2" />
                          Update Password
                        </>
                      )}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
                      }}
                      className="rounded-xl px-6"
                      data-testid="cancel-password-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-gray-700 font-medium">Password</span>
                      <p className="text-sm text-gray-500">Last changed: Unknown</p>
                    </div>
                  </div>
                  <span className="text-gray-400">••••••••</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Admin Quick Access */}
          {user.is_admin && (
            <Card className="rounded-3xl shadow-lg border-0 overflow-hidden" data-testid="admin-quick-access">
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-700" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-gray-900">Admin Access</h2>
                    <p className="text-sm text-gray-500">Quick links to admin features</p>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Button 
                    asChild 
                    variant="outline" 
                    className="h-auto py-4 rounded-xl justify-start"
                    data-testid="admin-dashboard-link"
                  >
                    <Link to="/admin" className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-primary-700" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">Admin Dashboard</p>
                        <p className="text-xs text-gray-500">Manage all content</p>
                      </div>
                    </Link>
                  </Button>
                  <Button 
                    asChild 
                    variant="outline" 
                    className="h-auto py-4 rounded-xl justify-start"
                    data-testid="media-gallery-link"
                  >
                    <Link to="/admin/media-gallery" className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-emerald-700" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">Media Gallery</p>
                        <p className="text-xs text-gray-500">Upload and manage media</p>
                      </div>
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
